import React, { useState } from 'react';
import { useT } from '../BuilderApp.jsx';
import WizardShell from '../WizardShell.jsx';

function StepNextHint({ step }) {
  const t = useT();
  const msg = t[`next${step}`];
  if (!msg) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:'.4rem',justifyContent:'center',marginTop:'.55rem',fontSize:'.72rem',color:'rgba(148,163,184,.55)',letterSpacing:'.01em',lineHeight:1.4}}>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{flexShrink:0,opacity:.55}}>
        <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{msg}</span>
    </div>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      onClick={copy}
      style={{
        background: copied ? 'rgba(52,211,153,.15)' : 'rgba(255,255,255,.06)',
        border: `1px solid ${copied ? 'rgba(52,211,153,.35)' : 'rgba(255,255,255,.12)'}`,
        borderRadius: 6, padding: '.15rem .45rem',
        fontSize: '.65rem', fontWeight: 700,
        color: copied ? '#34d399' : 'var(--dim)',
        cursor: 'pointer', transition: 'all .2s', flexShrink: 0,
      }}
    >
      {copied ? '✓' : 'Copy'}
    </button>
  );
}

function DevHelp({ domain, email, lang }) {
  const t = useT();
  const [state, setState] = useState('idle');
  const [ticket, setTicket] = useState(null);

  const submit = async () => {
    setState('submitting');
    // Mock: no fetch — generate local ticket reference
    await new Promise(r => setTimeout(r, 900));
    setTicket(`CHM-${Math.floor(1000 + Math.random() * 9000)}`);
    setState('done');
  };

  return (
    <div style={{
      border: '1px solid rgba(129,140,248,.2)',
      borderRadius: 12, padding: '.85rem 1rem',
      background: 'rgba(129,140,248,.05)',
    }}>
      {state === 'idle' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem' }}>
          <div style={{ fontSize: '.79rem', color: 'var(--muted)', lineHeight: 1.45 }}>
            <strong style={{ color: 'var(--text)' }}>{t.dns_heading}</strong><br/>
            {t.dns_sub}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={submit} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {t.dns_btn} ↗
          </button>
        </div>
      )}

      {state === 'submitting' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '.65rem', fontSize: '.79rem', color: 'var(--muted)' }}>
          <svg style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5.5" stroke="rgba(255,255,255,.15)" strokeWidth="1.5"/>
            <path d="M7 1.5a5.5 5.5 0 015.5 5.5" stroke="var(--blue2)" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {t.dns_sending}
        </div>
      )}

      {state === 'done' && (
        <div style={{ fontSize: '.79rem', lineHeight: 1.6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.45rem' }}>
            <span style={{ color: '#34d399', fontWeight: 800 }}>✓</span>
            <strong style={{ color: 'var(--text)' }}>{t.dns_done_h}</strong>
            <span style={{
              background: 'rgba(129,140,248,.15)', color: '#818cf8',
              border: '1px solid rgba(129,140,248,.3)',
              borderRadius: 6, padding: '.1rem .45rem',
              fontSize: '.68rem', fontWeight: 800, letterSpacing: '.04em',
              marginLeft: 'auto', flexShrink: 0,
            }}>{ticket}</span>
          </div>
          <p style={{ color: 'var(--muted)', margin: '0 0 .6rem' }}>{t.dns_done_sub(ticket, email)}</p>
          <a
            href={`https://status.chamcore.com/ticket/${ticket}`}
            target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--blue2)', fontSize: '.75rem', fontWeight: 600, textDecoration: 'none' }}
          >
            {t.dns_status_lbl} → {ticket}
          </a>
        </div>
      )}

      {state === 'error' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem' }}>
          <span style={{ fontSize: '.79rem', color: '#f87171' }}>{t.dns_err}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setState('idle')} style={{ flexShrink: 0 }}>↩</button>
        </div>
      )}
    </div>
  );
}

export default function Step7Domain({ config, setField, next, back, lang, setLang }) {
  const t = useT();
  const [mode, setMode] = useState(config.custom_domain ? 'custom' : 'auto');
  const no = ['no', 'sv', 'da'].includes(lang);

  const autoSlug = (config.branding.company_name || 'mybusiness')
    .toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'mybusiness';

  const canContinue = mode === 'auto' || config.custom_domain.trim().length > 3;

  const dnsRows = [
    { label: 'Type',            value: 'CNAME',              color: '#34d399' },
    { label: 'Name / Host',     value: '@',                  color: '#fbbf24' },
    { label: 'Value / Target',  value: 'sites.chamcore.com', color: '#60a5fa' },
    { label: 'TTL',             value: '3600',               color: 'var(--muted)' },
  ];

  const handleContinue = () => {
    if (mode === 'auto') setField('slug', autoSlug);
    next();
  };

  return (
    <WizardShell step={7} total={8} title={t.s6_domain_title || t.s6_title} subtitle={t.s6_domain_sub || t.s6_sub} onBack={back} lang={lang} setLang={setLang}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Mode selector */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,240px),1fr))', gap: '.7rem' }}>
          {[
            { v: 'auto',   icon: '⚡', label: t.s6_auto_label || 'We handle it', sub: t.s6_auto_sub || 'We create a professional URL for you.' },
            { v: 'custom', icon: '🌐', label: t.s6_cust_label || 'My own domain',  sub: t.s6_cust_sub || 'Use your existing domain name.' },
          ].map(opt => (
            <div
              key={opt.v}
              className={`radio-card${mode === opt.v ? ' selected' : ''}`}
              onClick={() => setMode(opt.v)}
              style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '.35rem' }}
            >
              <span style={{ fontSize: '1.2rem' }}>{opt.icon}</span>
              <span style={{ fontWeight: 700, fontSize: '.88rem' }}>{opt.label}</span>
              <span style={{ fontSize: '.73rem', color: 'var(--muted)', lineHeight: 1.4 }}>{opt.sub}</span>
            </div>
          ))}
        </div>

        {/* Auto mode confirmation */}
        {mode === 'auto' && (
          <div style={{ background: 'rgba(52,211,153,.07)', border: '1px solid rgba(52,211,153,.2)', borderRadius: 12, padding: '.9rem 1rem', display: 'flex', gap: '.65rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>✅</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.82rem', color: 'var(--green)', marginBottom: '.2rem' }}>
                {t.s6_handle}
              </div>
              <div style={{ fontSize: '.76rem', color: 'var(--muted)', lineHeight: 1.5 }}>
                {no
                  ? <><b>Nettsiden din er tilgjengelig på </b><strong style={{ color: 'var(--text)' }}>{autoSlug}.chamcore.site</strong> med én gang — du kan koble til ditt eget domene senere fra dashbordet.</>
                  : <>Your site will be live at <strong style={{ color: 'var(--text)' }}>{autoSlug}.chamcore.site</strong> immediately — connect your own domain anytime from your dashboard.</>
                }
              </div>
            </div>
          </div>
        )}

        {/* Custom domain mode */}
        {mode === 'custom' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label className="field-label">{t.s6_your_domain}</label>
              <input
                type="text"
                className="field-input"
                placeholder="yourbusiness.com"
                value={config.custom_domain}
                onChange={e => setField('custom_domain', e.target.value)}
              />
              <p style={{ fontSize: '.72rem', color: 'var(--dim)', marginTop: '.35rem' }}>{t.s6_auto_hint}</p>
            </div>

            {config.custom_domain && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ background: 'rgba(255,255,255,.04)', padding: '.8rem 1rem', fontSize: '.82rem', fontWeight: 700, borderBottom: '1px solid var(--border)' }}>
                  {t.s6_dns_title}
                </div>
                {(t.s6_dns || []).map((s, idx) => (
                  <div key={idx} style={{ padding: '.85rem 1rem', borderBottom: idx < (t.s6_dns.length - 1) ? '1px solid var(--border)' : undefined, display: 'flex', gap: '.7rem' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(59,130,246,.15)', border: '1px solid rgba(59,130,246,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', fontWeight: 800, color: 'var(--blue2)', flexShrink: 0, marginTop: '.05rem' }}>{idx + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '.82rem', marginBottom: '.3rem' }}>{s.t}</div>
                      {s.d && <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{s.d}</div>}
                      {s.dns && (
                        <div style={{ background: '#0c1220', border: '1px solid rgba(255,255,255,.08)', borderRadius: 9, padding: '.65rem .9rem', marginTop: '.4rem', fontFamily: 'monospace', fontSize: '.77rem' }}>
                          {dnsRows.map(r => (
                            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.18rem 0' }}>
                              <span style={{ color: 'var(--dim)', minWidth: 110, flexShrink: 0 }}>{r.label}</span>
                              <span style={{ color: r.color, flex: 1 }}>{r.value}</span>
                              <CopyBtn value={r.value}/>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <DevHelp domain={config.custom_domain} email={config.owner_email} lang={lang}/>
          </div>
        )}

        <button className="btn btn-primary btn-lg btn-full" onClick={handleContinue} disabled={!canContinue}>
          {t.continue}
        </button>
        <StepNextHint step={7}/>
      </div>
    </WizardShell>
  );
}
